'use client'
import * as React from 'react'
import PropTypes from 'prop-types'

// --- Lib ---
import { parseShortcutKeys } from '@tiptap-ui/lib/tiptap-utils'

// --- Hooks ---
import { useTiptapEditor } from '@tiptap-ui/hooks/use-tiptap-editor'

import {
  UNDO_REDO_SHORTCUT_KEYS,
  useUndoRedo,
} from '@tiptap-ui/components/tiptap-ui/undo-redo-button'

import { Button } from '@tiptap-ui/components/tiptap-ui-primitive/button'
import { Badge } from '@tiptap-ui/components/tiptap-ui-primitive/badge'

export function HistoryShortcutBadge ({
  action,
  shortcutKeys = UNDO_REDO_SHORTCUT_KEYS[action],
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>
}

HistoryShortcutBadge.propTypes = {
  action: PropTypes.string.isRequired,
  shortcutKeys: PropTypes.string,
}

/**
 * Button component for triggering undo/redo actions in a Tiptap editor.
 *
 * For custom button implementations, use the `useHistory` hook instead.
 */
export const UndoRedoButton = React.forwardRef((
  {
    editor: providedEditor,
    action,
    text,
    hideWhenUnavailable = false,
    onExecuted,
    showShortcut = false,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const { isVisible, handleAction, label, canExecute, Icon, shortcutKeys } =
    useUndoRedo({
      editor,
      action,
      hideWhenUnavailable,
      onExecuted,
    })

  const handleClick = React.useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleAction()
  }, [handleAction, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type='button'
      disabled={!canExecute}
      data-style='ghost'
      data-disabled={!canExecute}
      role='button'
      tabIndex={-1}
      aria-label={label}
      tooltip={label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}
    >
      {children ?? (
        <>
          <Icon className='tiptap-button-icon' />
          {text && <span className='tiptap-button-text'>{text}</span>}
          {showShortcut && (
            <HistoryShortcutBadge action={action} shortcutKeys={shortcutKeys} />
          )}
        </>
      )}
    </Button>
  )
})

UndoRedoButton.displayName = 'UndoRedoButton'
UndoRedoButton.propTypes = {
  editor: PropTypes.object,
  action: PropTypes.string.isRequired,
  text: PropTypes.string,
  hideWhenUnavailable: PropTypes.bool,
  onExecuted: PropTypes.func,
  showShortcut: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
}
